import React from "react";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import styles from "../../../css/ai-mode/sidebar/sidebar.module.css"

import { ConversationModel } from "../../../models/platform/conversationModel";

interface SidebarProps {
  isOpen: boolean;
  conversations: ConversationModel[];
  loading: boolean;
  errorMessage: string | null;
  activeConversationId: string | null;
  currentlyEditingId: string | null;
  editingName: string;
  menuAnchorEl: HTMLElement | null;
  selectedConversationId: string | null;

  // event callbacks
  onToggle: () => void;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
  onMenuOpen: (e: React.MouseEvent<HTMLButtonElement>, id: string) => void;
  onMenuClose: () => void;
  onRename: () => void;
  onSaveName: (id: string) => void;
  onDelete: (id: string | null) => void;
  onEditingNameChange: (val: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  conversations,
  loading,
  errorMessage,
  activeConversationId,
  currentlyEditingId,
  editingName,
  menuAnchorEl,
  selectedConversationId,
  onToggle,
  onNewChat,
  onSelectConversation,
  onMenuOpen,
  onMenuClose,
  onRename,
  onSaveName,
  onDelete,
  onEditingNameChange,
}) => {
  return (
    <aside className={`${styles.aiSidebar} ${isOpen ? "" : styles.sidebarClosed}`}>
      <div className={styles.sidebarHeader}>
        <button className={styles.hamburgerButton} onClick={onToggle}>
          <MenuIcon />
        </button>
      </div>

      <button className={styles.newChatButton} onClick={onNewChat}>
        <AddIcon style={{ marginRight: 6 }} />
        New chat
      </button>

      <div className={styles.sidebarSectionTitle}>Recent</div>

      {loading ? (
        <div className={styles.sidebarLoading}>Loading conversations...</div>
      ) : errorMessage ? (
        <div className={styles.sidebarError}>{errorMessage}</div>
      ) : (
        <>
          <ul className={styles.chatList}>
            {conversations.map((conv, index) => {
              const validId =
                typeof conv._id === "string" && conv._id.length === 24
                  ? conv._id
                  : `conv-${index}`;
              const isActive = activeConversationId === conv._id;

              return (
                <li
                  key={validId}
                  className={`${styles.chatItem} ${isActive ? styles.chatItemActive : ""}`}
                  onClick={() => onSelectConversation(conv._id)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    onSelectConversation(conv._id);
                  }}
                >
                  {currentlyEditingId === conv._id ? (
                    <input
                      className={styles.chatItemInput}
                      value={editingName}
                      autoFocus
                      onChange={(e) => onEditingNameChange(e.target.value)}
                      onBlur={() => onSaveName(conv._id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveName(conv._id);
                      }}
                    />
                  ) : (
                    <>
                      <span className={styles.chatItemTitle}>
                        {conv.conversation_name || "Untitled conversation"}
                      </span>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMenuOpen(e, validId);
                        }}
                        className={styles.chatItemMenuButton}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={onMenuClose}>
            <MenuItem onClick={onRename}>Rename</MenuItem>
            <MenuItem
              onClick={() => onDelete(selectedConversationId)}
              style={{ color: "red" }}
            >
              Delete
            </MenuItem>
          </Menu>
        </>
      )}
    </aside>
  );
};

export default Sidebar;
